import { useEffect, useRef, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import axiosService from '../../utils/axios';

// Simple debounce implementation
const debounce = (fn: Function, delay: number) => {
    let timeoutId: NodeJS.Timeout | null = null;

    return (...args: any[]) => {
        if (timeoutId) {
            clearTimeout(timeoutId);
        }

        timeoutId = setTimeout(() => {
            fn(...args);
            timeoutId = null;
        }, delay);
    };
};

const ActivityTracker = () => {
    const user = useSelector((state: RootState) => state.auth.user);
    const lastActivityTimestamp = useRef<number>(Date.now());
    const isActive = useRef<boolean>(true);
    const updateInProgress = useRef<boolean>(false);
    const lastPathname = useRef<string>(window.location.pathname);

    // Create a memoized, debounced function for updating user activity
    // This prevents multiple simultaneous calls and rapid consecutive calls
    const debouncedUpdateActivity = useCallback(
        debounce(async () => {
            try {
                if (!user?._id || updateInProgress.current) return;

                updateInProgress.current = true;

                // Call backend to update user activity
                await axiosService.post('/user/updateActivity');

                // Update local timestamp reference
                lastActivityTimestamp.current = Date.now();
            } catch (error) {
                console.error('Error updating user activity:', error);
            } finally {
                updateInProgress.current = false;
            }
        }, 300), // 300ms debounce delay - enough to prevent duplicates but not too long
        [user?._id]
    );

    // Simplified function to check if we should update activity
    const updateUserActivity = useCallback(() => {
        if (!user?._id) return;

        const now = Date.now();
        // Only update if more than 30 minutes have passed since last activity
        if (now - lastActivityTimestamp.current > 30 * 60 * 1000) {
            debouncedUpdateActivity();
        }
    }, [user?._id, debouncedUpdateActivity]);

    // Track user's initial load and detect route changes manually
    useEffect(() => {
        if (!user?._id) return;

        // Always update on initial load
        debouncedUpdateActivity();

        // Set up a MutationObserver to track SPA navigation changes
        // This works without router context by observing DOM changes
        const navigationObserver = new MutationObserver(() => {
            const currentPathname = window.location.pathname;
            if (currentPathname !== lastPathname.current) {
                lastPathname.current = currentPathname;
                debouncedUpdateActivity();
            }
        });

        // Observe changes to document title or body which usually change during navigation
        navigationObserver.observe(document.querySelector('title') || document.body, {
            subtree: true,
            childList: true
        });

        return () => {
            navigationObserver.disconnect();
        };
    }, [user?._id, debouncedUpdateActivity]);

    // Track user activity events
    useEffect(() => {
        if (!user?._id) return;

        const handleActivity = () => {
            isActive.current = true;
            updateUserActivity();
        };

        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                updateUserActivity();
            }
        };

        // Track user interaction events
        window.addEventListener('mousemove', handleActivity);
        window.addEventListener('keydown', handleActivity);
        window.addEventListener('click', handleActivity);
        window.addEventListener('scroll', handleActivity);
        document.addEventListener('visibilitychange', handleVisibilityChange);

        // Track history API for SPA navigation - works without router context
        const originalPushState = window.history.pushState;
        const originalReplaceState = window.history.replaceState;

        window.history.pushState = function() {
            originalPushState.apply(this, arguments as any);
            debouncedUpdateActivity(); // Track when navigation happens
        };

        window.history.replaceState = function() {
            originalReplaceState.apply(this, arguments as any);
            debouncedUpdateActivity(); // Track when navigation happens
        };

        // Listen for popstate events (back/forward navigation)
        window.addEventListener('popstate', debouncedUpdateActivity);

        // Update activity periodically if user remains active
        const intervalId = setInterval(() => {
            if (isActive.current) {
                debouncedUpdateActivity();
                isActive.current = false; // Reset active flag
            }
        }, 30 * 60 * 1000);

        return () => {
            // Clean up event listeners
            window.removeEventListener('mousemove', handleActivity);
            window.removeEventListener('keydown', handleActivity);
            window.removeEventListener('click', handleActivity);
            window.removeEventListener('scroll', handleActivity);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            window.removeEventListener('popstate', debouncedUpdateActivity);

            // Restore original history methods
            window.history.pushState = originalPushState;
            window.history.replaceState = originalReplaceState;

            clearInterval(intervalId);
        };
    }, [user?._id, debouncedUpdateActivity, updateUserActivity]);

    return null; // This component doesn't render anything
};

export default ActivityTracker;
